import { useEffect, useState } from "react";
import { Percent, ChevronDown, Loader2, Plus, Trash2, Car, Package, Users, Target, Wrench, CalendarRange, Repeat } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import BranchOverrideToggle from "./dealer/BranchOverrideToggle";
import { useAutosave } from "../hooks/useAutosave";
import SaveStatusIndicator from "./dealer/SaveStatusIndicator";
import useSettingsExpandAll from "../lib/useSettingsExpandAll";

/**
 * CommissionConfigSection — Provisions-Einstellungen des Händlers (Phase C, Juni-2026).
 * 1) Fahrzeug-Provisionsregeln (Filter + EUR/%)
 * 2) WMS-Kategorie-übergreifende Provision in %
 * 3) Verteilung als feste Anteile pro Tätigkeitsbereich
 */
const CATEGORY_OPTS = [
    { value: "", label: "Alle Kategorien" },
    { value: "motorcycle", label: "Motorrad" },
    { value: "bicycle", label: "Fahrrad" },
];

/** Gültigkeits-Zeitraum (von/bis) einer Provisionsregel. */
function ValidityRange({ r, onChange, idx, prefix }) {
    return (
        <div className="flex flex-wrap items-end gap-2 mt-2 pt-2 border-t border-dashed border-gray-200" data-testid={`${prefix}-validity-${idx}`}>
            <CalendarRange className="w-3.5 h-3.5 text-gray-400 mb-2" />
            <div>
                <label className="text-[10px] uppercase font-bold text-gray-500 block">Gültig von</label>
                <Input type="date" value={r.valid_from || ""} onChange={(e) => onChange({ valid_from: e.target.value || null })} className="h-8 rounded-sm text-xs" data-testid={`${prefix}-valid-from-${idx}`} />
            </div>
            <div>
                <label className="text-[10px] uppercase font-bold text-gray-500 block">Gültig bis</label>
                <Input type="date" value={r.valid_to || ""} onChange={(e) => onChange({ valid_to: e.target.value || null })} className="h-8 rounded-sm text-xs" data-testid={`${prefix}-valid-to-${idx}`} />
            </div>
            <span className="text-[10px] text-gray-400 mb-2">leer = unbegrenzt</span>
        </div>
    );
}

/** Liste von Zubehör-/Dienstleistungs-Provisionsregeln (Name, €/%, Zeitraum). */
function ExtraCommissionRules({ title, hint, icon: Icon, rules, onChange, prefix }) {
    const add = () => onChange([...(rules || []), {
        id: crypto.randomUUID?.() || `new-${Date.now()}-${Math.random()}`,
        name: "Neue Regel", commission_type: "percent", amount: 0, valid_from: null, valid_to: null,
    }]);
    const upd = (i, patch) => onChange(rules.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
    const rm = (i) => onChange(rules.filter((_, idx) => idx !== i));
    return (
        <div data-testid={`${prefix}-rules`}>
            <div className="flex items-center justify-between mb-2">
                <h4 className="font-bold text-sm flex items-center gap-2"><Icon className="w-4 h-4 text-gray-500" /> {title}</h4>
                <Button onClick={add} variant="outline" className="h-8 rounded-sm text-xs" data-testid={`${prefix}-add`}><Plus className="w-3.5 h-3.5 mr-1" /> Regel</Button>
            </div>
            <p className="text-[11px] text-gray-500 mb-3">{hint}</p>
            {(rules || []).length === 0 ? (
                <div className="text-xs text-gray-500 bg-gray-50 border border-dashed border-gray-300 rounded-sm p-3" data-testid={`${prefix}-empty`}>Keine Regel. „+ Regel" fügt eine hinzu.</div>
            ) : (
                <div className="space-y-3">
                    {rules.map((r, i) => (
                        <div key={r.id} className="border border-gray-200 rounded-sm p-3" data-testid={`${prefix}-rule-${i}`}>
                            <div className="flex items-end gap-2">
                                <div className="flex-1">
                                    <label className="text-[10px] uppercase font-bold text-gray-500 block">Regelname</label>
                                    <Input value={r.name} onChange={(e) => upd(i, { name: e.target.value })} placeholder="Regelname" className="h-8 rounded-sm font-bold text-sm" data-testid={`${prefix}-name-${i}`} />
                                </div>
                                <div>
                                    <label className="text-[10px] uppercase font-bold text-gray-500 block">Provision</label>
                                    <div className="flex gap-1">
                                        <Input type="number" value={r.amount} onChange={(e) => upd(i, { amount: e.target.value })} className="h-8 rounded-sm font-mono-num w-24" data-testid={`${prefix}-amount-${i}`} />
                                        <select value={r.commission_type} onChange={(e) => upd(i, { commission_type: e.target.value })} className="h-8 border border-gray-300 rounded-sm text-sm px-1" data-testid={`${prefix}-type-${i}`}>
                                            <option value="percent">%</option>
                                            <option value="eur">€</option>
                                        </select>
                                    </div>
                                </div>
                                <button onClick={() => rm(i)} className="p-1.5 rounded-sm hover:bg-red-50 mb-0.5" data-testid={`${prefix}-remove-${i}`}><Trash2 className="w-4 h-4 text-red-500" /></button>
                            </div>
                            <ValidityRange r={r} onChange={(patch) => upd(i, patch)} idx={i} prefix={prefix} />
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default function CommissionConfigSection({ currentUser }) {
    const [expanded, setExpanded] = useState(false);
    useSettingsExpandAll(setExpanded);
    const [loading, setLoading] = useState(false);
    const [config, setConfig] = useState(null);
    const [meta, setMeta] = useState({ categories: [], activity_areas: [] });

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/dealer/commission-config");
            setConfig(data.config);
            setMeta(data.meta);
        } catch {
            toast.error("Konfiguration konnte nicht geladen werden");
        } finally { setLoading(false); }
    };

    useEffect(() => {
        if (expanded && !config && !loading) load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [expanded]);

    if (!currentUser || currentUser.role !== "b2b") return null;

    return <CommissionConfigBody
        expanded={expanded} setExpanded={setExpanded}
        loading={loading} config={config} setConfig={setConfig} meta={meta}
        currentUser={currentUser} />;
}

function CommissionConfigBody({ expanded, setExpanded, loading, config, setConfig, meta, currentUser }) {
    // Jul-2026 (User-Wunsch): Autospeichern statt manuellem „Speichern"-Button.
    const saveSilent = async (current) => {
        try {
            await api.put("/dealer/commission-config", { config: current });
        } catch (e) {
            toast.error("Automatisches Speichern fehlgeschlagen");
            throw e;
        }
    };
    const autosave = useAutosave(config, saveSilent,
        { delay: 1200, enabled: expanded && !loading && !!config });

    const addRule = () => setConfig((c) => ({
        ...c,
        vehicle_rules: [...(c.vehicle_rules || []), { id: (crypto.randomUUID?.() || `new-${Date.now()}-${Math.random()}`), name: "Neue Regel", filter: {}, commission_type: "percent", amount: 0 }],
    }));
    const updateRule = (i, patch) => setConfig((c) => ({
        ...c, vehicle_rules: c.vehicle_rules.map((r, idx) => (idx === i ? { ...r, ...patch } : r)),
    }));
    const updateRuleFilter = (i, key, val) => setConfig((c) => ({
        ...c, vehicle_rules: c.vehicle_rules.map((r, idx) => (idx === i ? { ...r, filter: { ...r.filter, [key]: val } } : r)),
    }));
    const removeRule = (i) => setConfig((c) => ({ ...c, vehicle_rules: c.vehicle_rules.filter((_, idx) => idx !== i) }));

    const distSum = config ? Object.values(config.distribution || {}).reduce((a, b) => a + (Number(b) || 0), 0) : 0;

    return (
        <div className="bg-white border border-gray-200 rounded-sm" data-testid="commission-config-section">
            <div className="flex items-center border-b border-gray-200">
                <button onClick={() => setExpanded((e) => !e)} aria-expanded={expanded} className={`flex-1 flex items-center gap-3 px-6 py-4 text-left hover:bg-gray-50 transition-colors min-w-0`} data-testid="commission-config-toggle">
                    <Percent className="w-5 h-5 text-[#FFCC00] shrink-0" />
                    <div className="flex-1 min-w-0">
                        <h2 className="font-display text-lg font-bold">Provisionen</h2>
                        <p className="text-xs text-gray-600 mt-1">Fahrzeug-Provision (Filter, €/%), WMS-Kategorie-% und Verteilung auf Tätigkeitsbereiche.</p>
                    </div>
                    <span className={`text-gray-400 text-lg transition-transform shrink-0 ${expanded ? "rotate-180" : ""}`} aria-hidden="true">⌄</span>
                </button>
                <div className="pr-5 pl-2 shrink-0">
                    <BranchOverrideToggle
                        area="provisionen"
                        currentUser={currentUser}
                        infoText="Provisions-Einstellungen des Hauptsitzes auf alle Filialen übernehmen & überschreiben."
                        testId="provisionen-branch-override"
                    />
                </div>
            </div>

            {expanded && (
                <div className="p-5 border-t border-gray-100 space-y-6">
                    {loading || !config ? (
                        <Loader2 className="w-5 h-5 animate-spin text-gray-400 mx-auto" />
                    ) : (
                        <>
                            {/* 1) Fahrzeug-Provisionsregeln */}
                            <div data-testid="commission-vehicle-rules">
                                <div className="flex items-center justify-between mb-2">
                                    <h4 className="font-bold text-sm flex items-center gap-2"><Car className="w-4 h-4 text-gray-500" /> Fahrzeug-Provision (per Filter)</h4>
                                    <Button onClick={addRule} variant="outline" className="h-8 rounded-sm text-xs" data-testid="commission-add-rule"><Plus className="w-3.5 h-3.5 mr-1" /> Regel</Button>
                                </div>
                                <p className="text-[11px] text-gray-500 mb-3">Erste passende Regel gilt. Filter optional — leere Filter greifen für alle Fahrzeuge.</p>
                                {(config.vehicle_rules || []).length === 0 ? (
                                    <div className="text-xs text-gray-500 bg-gray-50 border border-dashed border-gray-300 rounded-sm p-3" data-testid="commission-rules-empty">Noch keine Regel. „+ Regel" fügt eine hinzu.</div>
                                ) : (
                                    <div className="space-y-3">
                                        {config.vehicle_rules.map((r, i) => (
                                            <div key={r.id} className="border border-gray-200 rounded-sm p-3" data-testid={`commission-rule-${i}`}>
                                                <div className="flex items-center gap-2 mb-2">
                                                    <Input value={r.name} onChange={(e) => updateRule(i, { name: e.target.value })} placeholder="Regelname" className="h-8 rounded-sm font-bold text-sm flex-1" data-testid={`commission-rule-name-${i}`} />
                                                    <button onClick={() => removeRule(i)} className="p-1.5 rounded-sm hover:bg-red-50" data-testid={`commission-rule-remove-${i}`}><Trash2 className="w-4 h-4 text-red-500" /></button>
                                                </div>
                                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                                    <div>
                                                        <label className="text-[10px] uppercase font-bold text-gray-500">Kategorie</label>
                                                        <select value={r.filter?.category || ""} onChange={(e) => updateRuleFilter(i, "category", e.target.value)} className="w-full h-8 border border-gray-300 rounded-sm text-sm px-2" data-testid={`commission-rule-category-${i}`}>
                                                            {CATEGORY_OPTS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                                                        </select>
                                                    </div>
                                                    <div>
                                                        <label className="text-[10px] uppercase font-bold text-gray-500">Marke</label>
                                                        <Input value={r.filter?.brand || ""} onChange={(e) => updateRuleFilter(i, "brand", e.target.value)} placeholder="egal" className="h-8 rounded-sm" data-testid={`commission-rule-brand-${i}`} />
                                                    </div>
                                                    <div>
                                                        <label className="text-[10px] uppercase font-bold text-gray-500">Zustand</label>
                                                        <Input value={r.filter?.condition || ""} onChange={(e) => updateRuleFilter(i, "condition", e.target.value)} placeholder="egal" className="h-8 rounded-sm" data-testid={`commission-rule-condition-${i}`} />
                                                    </div>
                                                    <div>
                                                        <label className="text-[10px] uppercase font-bold text-gray-500">Preis ab €</label>
                                                        <Input type="number" value={r.filter?.min_price ?? ""} onChange={(e) => updateRuleFilter(i, "min_price", e.target.value)} placeholder="0" className="h-8 rounded-sm font-mono-num" data-testid={`commission-rule-minprice-${i}`} />
                                                    </div>
                                                    <div>
                                                        <label className="text-[10px] uppercase font-bold text-gray-500">Preis bis €</label>
                                                        <Input type="number" value={r.filter?.max_price ?? ""} onChange={(e) => updateRuleFilter(i, "max_price", e.target.value)} placeholder="∞" className="h-8 rounded-sm font-mono-num" data-testid={`commission-rule-maxprice-${i}`} />
                                                    </div>
                                                    <div>
                                                        <label className="text-[10px] uppercase font-bold text-gray-500">Provision</label>
                                                        <div className="flex gap-1">
                                                            <Input type="number" value={r.amount} onChange={(e) => updateRule(i, { amount: e.target.value })} className="h-8 rounded-sm font-mono-num flex-1" data-testid={`commission-rule-amount-${i}`} />
                                                            <select value={r.commission_type} onChange={(e) => updateRule(i, { commission_type: e.target.value })} className="h-8 border border-gray-300 rounded-sm text-sm px-1" data-testid={`commission-rule-type-${i}`}>
                                                                <option value="percent">%</option>
                                                                <option value="eur">€</option>
                                                            </select>
                                                        </div>
                                                    </div>
                                                </div>
                                                <ValidityRange r={r} onChange={(patch) => updateRule(i, patch)} idx={i} prefix="commission-rule" />
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* 2) WMS-Kategorie-% */}
                            <div data-testid="commission-category-percents">
                                <h4 className="font-bold text-sm flex items-center gap-2 mb-2"><Package className="w-4 h-4 text-gray-500" /> WMS-Kategorie-Provision (%)</h4>
                                <p className="text-[11px] text-gray-500 mb-3">Übergeordnete Provision je Warenwirtschafts-Kategorie — gilt für alle Artikel der Kategorie.</p>
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                    {meta.categories.map((c) => (
                                        <div key={c.value}>
                                            <label className="text-[10px] uppercase font-bold text-gray-500">{c.label}</label>
                                            <div className="flex items-center gap-1">
                                                <Input type="number" min="0" max="100" value={config.category_percents?.[c.value] ?? 0} onChange={(e) => setConfig((cf) => ({ ...cf, category_percents: { ...cf.category_percents, [c.value]: e.target.value } }))} className="h-8 rounded-sm font-mono-num" data-testid={`commission-cat-${c.value}`} />
                                                <span className="text-xs text-gray-400">%</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* 3) Verteilung pro Tätigkeitsbereich */}
                            <div data-testid="commission-distribution">
                                <h4 className="font-bold text-sm flex items-center gap-2 mb-2"><Users className="w-4 h-4 text-gray-500" /> Verteilung auf Tätigkeitsbereiche (%)</h4>
                                <p className="text-[11px] text-gray-500 mb-3">Feste Anteile, wie die Provision auf die Bereiche verteilt wird. Summe sollte 100 % ergeben.</p>
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                    {meta.activity_areas.map((a) => (
                                        <div key={a.value}>
                                            <label className="text-[10px] uppercase font-bold text-gray-500">{a.label}</label>
                                            <div className="flex items-center gap-1">
                                                <Input type="number" min="0" max="100" value={config.distribution?.[a.value] ?? 0} onChange={(e) => setConfig((cf) => ({ ...cf, distribution: { ...cf.distribution, [a.value]: e.target.value } }))} className="h-8 rounded-sm font-mono-num" data-testid={`commission-dist-${a.value}`} />
                                                <span className="text-xs text-gray-400">%</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                <div className={`text-xs font-bold mt-2 ${Math.abs(distSum - 100) < 0.01 ? "text-green-600" : "text-amber-600"}`} data-testid="commission-dist-sum">
                                    Summe: {distSum.toFixed(0)} %{Math.abs(distSum - 100) >= 0.01 && " (sollte 100 % sein)"}
                                </div>
                            </div>

                            {/* 4) Provision auf Zusatzerträge (Zinsen/Versicherung) */}
                            <div data-testid="commission-extra-earnings">
                                <h4 className="font-bold text-sm flex items-center gap-2 mb-2"><Percent className="w-4 h-4 text-gray-500" /> Provision auf Zusatzerträge (%)</h4>
                                <p className="text-[11px] text-gray-500 mb-3">Provisionssatz des Verkäufers auf den je Vorgang erfassten „Zusatzertrag (Zinsen/Versicherung)".</p>
                                <div className="flex items-center gap-1 max-w-[160px]">
                                    <Input type="number" min="0" max="100" step="0.1" value={config.extra_earnings_percent ?? 0}
                                        onChange={(e) => setConfig((cf) => ({ ...cf, extra_earnings_percent: e.target.value }))}
                                        className="h-8 rounded-sm font-mono-num" data-testid="commission-extra-earnings-percent" />
                                    <span className="text-xs text-gray-400">%</span>
                                </div>
                            </div>

                            {/* 4a) Inzahlungnahme-Provision (Jul-2026, User-Wunsch) */}
                            <div data-testid="commission-tradein">
                                <h4 className="font-bold text-sm flex items-center gap-2 mb-2"><Repeat className="w-4 h-4 text-gray-500" /> Inzahlungnahme-Provision (€)</h4>
                                <p className="text-[11px] text-gray-500 mb-3">Feste Provision je erfasstem Eintausch-Fahrzeug. Wird dem Verkäufer im Verkaufsprozess als Anreiz angezeigt (Hinweis-Popups bei der Inzahlungnahme-Entscheidung).</p>
                                <div className="flex items-center gap-1 max-w-[160px]">
                                    <Input type="number" min="0" step="1" value={config.tradein_commission_eur ?? 0}
                                        onChange={(e) => setConfig((cf) => ({ ...cf, tradein_commission_eur: e.target.value }))}
                                        className="h-8 rounded-sm font-mono-num" data-testid="commission-tradein-eur" />
                                    <span className="text-xs text-gray-400">€</span>
                                </div>
                            </div>


                            <ExtraCommissionRules
                                title="Zubehör-Provision (mitverkauft)"
                                hint="Provision auf mitverkauftes Zubehör — pro Regel € (pauschal) oder % vom Zubehör-Umsatz, mit Gültigkeits-Zeitraum. Erste aktive Regel gilt."
                                icon={Package}
                                rules={config.accessory_rules || []}
                                onChange={(rules) => setConfig((c) => ({ ...c, accessory_rules: rules }))}
                                prefix="commission-accessory"
                            />
                            <ExtraCommissionRules
                                title="Dienstleistungs-Provision (mitverkauft)"
                                hint="Provision auf mitverkaufte Dienstleistungen — pro Regel € (pauschal) oder % vom Dienstleistungs-Umsatz, mit Gültigkeits-Zeitraum. Erste aktive Regel gilt."
                                icon={Wrench}
                                rules={config.service_rules || []}
                                onChange={(rules) => setConfig((c) => ({ ...c, service_rules: rules }))}
                                prefix="commission-service"
                            />

                            {/* 5) Monatsziele je Verkäufer (zentral hier gepflegt) */}
                            <SellerMonthlyTargets />

                            <div className="flex justify-end items-center gap-2" data-testid="commission-autosave-status">
                                <span className="text-[11px] text-gray-400">Änderungen werden automatisch gespeichert</span>
                                <SaveStatusIndicator savedAt={autosave.savedAt} dirty={autosave.dirty} saving={autosave.saving} />
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
}

/**
 * SellerMonthlyTargets — zentrale Pflege der Monats-UMSATZziele je Verkäufer
 * (eine Quelle der Wahrheit; angezeigt in Auswertungen & Provisions-Abrechnung).
 * Jul-2026 (User-Wunsch): Autospeichern statt „OK"-Button je Zeile.
 */
function SellerMonthlyTargets() {
    const [rows, setRows] = useState(null);
    const [dirtyIds, setDirtyIds] = useState(() => new Set());

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/dealer/analytics/seller-settings");
                setRows(data.sellers || []);
            } catch {
                setRows([]);
            }
        })();
    }, []);

    const setField = (id, value) => {
        setRows((rs) => rs.map((r) => (r.seller_id === id ? { ...r, monthly_target_net: value } : r)));
        setDirtyIds((s) => new Set(s).add(id));
    };

    const saveDirty = async (current) => {
        const ids = [...dirtyIds];
        setDirtyIds(new Set());
        try {
            for (const id of ids) {
                const r = (current || []).find((x) => x.seller_id === id);
                if (!r) continue;
                await api.put(`/dealer/analytics/seller-settings/${r.seller_id}`, {
                    provision_percent: Number(r.provision_percent) || 0,
                    monthly_target_net: Number(r.monthly_target_net) || 0,
                });
            }
        } catch (e) {
            toast.error("Automatisches Speichern der Umsatzziele fehlgeschlagen");
            throw e;
        }
    };
    const autosave = useAutosave(rows, saveDirty, { delay: 1000, enabled: rows !== null });

    return (
        <div data-testid="commission-monthly-targets">
            <div className="flex items-center justify-between mb-2">
                <h4 className="font-bold text-sm flex items-center gap-2"><Target className="w-4 h-4 text-gray-500" /> Monats-Umsatzziel je Verkäufer (€)</h4>
                <SaveStatusIndicator savedAt={autosave.savedAt} dirty={autosave.dirty} saving={autosave.saving} />
            </div>
            <p className="text-[11px] text-gray-500 mb-3">Netto-Umsatzziel pro Monat — Zielerreichung wird gegen den Umsatz des Verkäufers gerechnet (Verkäufer-Ranking &amp; Provisions-Abrechnung). Änderungen werden automatisch gespeichert.</p>
            {rows === null ? (
                <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
            ) : rows.length === 0 ? (
                <div className="text-xs text-gray-500 bg-gray-50 border border-dashed border-gray-300 rounded-sm p-3" data-testid="commission-targets-empty">Noch keine Verkäufer/Bearbeiter erfasst.</div>
            ) : (
                <div className="space-y-2 max-w-xl">
                    {rows.map((r) => (
                        <div key={r.seller_id} className="flex items-center gap-2" data-testid={`target-row-${r.seller_id}`}>
                            <span className="flex-1 min-w-0 truncate">
                                <span className="text-sm text-gray-800">{r.name}</span>
                                {r.site_code && (
                                    <span className="ml-2 inline-flex items-center text-[10px] font-bold uppercase tracking-wider text-gray-500 bg-gray-100 border border-gray-200 rounded-sm px-1.5 py-0.5 align-middle"
                                        title={r.site_name || r.site_code} data-testid={`target-site-${r.seller_id}`}>
                                        {r.site_code}
                                    </span>
                                )}
                            </span>
                            <Input type="number" min="0" step="100" value={r.monthly_target_net}
                                onChange={(e) => setField(r.seller_id, e.target.value)}
                                className="h-8 w-32 text-right rounded-sm font-mono-num" data-testid={`target-input-${r.seller_id}`} />
                            <span className="text-xs text-gray-400">€/Mon</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
